import { CSSProperties, useContext, useEffect, useState, Fragment } from "react";
import BrandDropdown from "components/ProfileDropdown/BrandDropdown";
import useBrand from 'Hooks/useBrand';
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import { useHistory, useLocation } from "react-router-dom";
import { clsx, getStatusToShowPaymentFailedBanner, makeKey, styx } from "modules/Utils";
import { SymphonyTooltip } from "modules/Sty";
import { NavigationItem } from "./Navbar";
import miniSymphonyLogo from "../../assets/images/logos/sidebar-collapsed.png";
import symphonyLogo from "../../assets/images/symphonyLogo.svg";
import AdminOptions from "./Components/AdminOptions";
import Indicator from "./Components/indicator";
import SubscriptionStatusIndicator from "./Components/SubscriptionStatusIndicator";
import useUser from 'Hooks/useUser';
import Colors from "modules/Colors";
import { NAV_BAR_WIDTH_COLLAPSED, NAV_BAR_WIDTH_EXPANDED, UserPermissionOnBrand } from "modules/Const";
import { useNavbar } from "Hooks/useNavbar";
import useScreen from "Hooks/useScreen";
import Icon from "elements/Icon";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import { OrganizationStatus } from "modules/Types";
import AdminId from "elements/AdminId";
import useMaestroChat from 'Hooks/useMaestroChat'
import ButtonSymphony from 'elements/ButtonSymphony'

interface BottomNavigationButton {
    icon: string;
    text: string;
    href: string;
    isAdminOnly?: boolean;
    onlyForSymphonyBetaTestUsers?: boolean;
}

const BOTTOM_NAVIGATION_BUTTONS: BottomNavigationButton[] = [
    {
        icon: 'gift',
        text: 'Refer & Earn',
        href: '/refer',
    },
    {
        icon: 'gear',
        text: 'Settings',
        href: '/settings',
    },
    // {
    //     icon: 'drag',
    //     text: 'Elements',
    //     href: '/elementspage',
    //     isAdminOnly: true,
    // },
    // {
    //     icon: 'website',
    //     text: 'Website Builder',
    //     href: '/website-builder'
    // },
]

interface DesktopNavigationProps {
    className: string;
    navigationItems: NavigationItem[];
    isPublic?: boolean;
}

const DesktopNavigation = ({
    className,
    navigationItems,
    isPublic = false,
}: DesktopNavigationProps) => {
    const history = useHistory();
    const location = useLocation();
    const { cssCalc } = useScreen();
    const {
        isCollapsed,
        setIsCollapsed,
        isHoverSmallSidebar,
        setIsHoverSmallSidebar,
        getIsSmallSidebar
    } = useNavbar();
    const {
        currentBrand,
        isProBrand, // Will be part of getShouldShowNewUserUpgradePrompt
        isLiteBrand, // Will be part of getShouldShowNewUserUpgradePrompt
        isStarterBrand,
        isGrowthBrand,
        isTeamBrand, // Will be part of getShouldShowNewUserUpgradePrompt
        isTeamV2Brand,
        getShouldShowNewUserUpgradePrompt // New selector
    } = useBrand();
    const {
        currentUser,
        proFreeTrialRedeemed,
        isSymphonyAdmin,
        isSymphonyBetaTestUser,
    } = useUser();
    const { organization, hasOrganizationAdminAccess } = useOrganizationV2();
    const { handleOpenModal } = useContext(UpgradeModalContext);
    const { demoMode, setDemoMode, devMode, setDevMode } = useMaestroChat();
    // free trial over status
    const freeTrialOver = proFreeTrialRedeemed()

    const [enablePro, setEnablePro] = useState(isProBrand)

    const selectedRoute = location.pathname

    const isOrganizationReadyOrWaiting = (
        organization?.setup_status === OrganizationStatus.READY ||
        organization?.setup_status === OrganizationStatus.WAITING_FOR_DETAILS) &&
        currentUser?.metadata?.organization_subscription?.status !== 'canceled';

    let hasViewOnlyAccess = currentBrand?.permissionLevel === UserPermissionOnBrand.VIEW_ONLY;
    if (hasOrganizationAdminAccess()) {
        hasViewOnlyAccess = false;
    }

    const isSmallSidebar = getIsSmallSidebar();

    const [dropdownOpenId, setDropdownOpenId] = useState<string | null>(null)

    useEffect(() => {
        if (isSymphonyAdmin) {
            setEnablePro(isProBrand)
        }
    }, [currentBrand])

    const handleUpgradeClick = () => {
        handleOpenModal({
            source: "Desktop Navigation - Upgrade to Pro Button"
        });
    }

    const renderUpgradePlanText = () => {
        if (hasViewOnlyAccess) {
            return '';
        }

        if (isOrganizationReadyOrWaiting) {
            return 'Upgrade to Pro'
        }

        if (isProBrand || isTeamBrand || isStarterBrand || isGrowthBrand || isTeamV2Brand) {
            return ''
        }

        const isUnpaid = getStatusToShowPaymentFailedBanner(currentBrand)

        if (isUnpaid) {
            return 'Reactivate Plan'
        } else if (freeTrialOver) {
            if (isLiteBrand) {
                return 'Upgrade to Pro'
            } else if (isStarterBrand) {
                return 'Upgrade to Growth'
            } else {
                return 'Upgrade Plan'
            }
        }

        return 'Upgrade Plan'
    }

    const renderLogoContainer = () => {
        return (
            <div
                className='logoContainer'
                style={{
                    marginBottom: 16,
                }}
            >
                {isCollapsed && isSmallSidebar &&
                    <img
                        className="miniSymphonyLogo"
                        style={sty.miniSymphonyLogo}
                        src={miniSymphonyLogo}
                        alt="miniSymphonyLogo"
                    />
                }
                {!isSmallSidebar &&
                    <div className="logoMainContainer" style={sty.logoMainContainer}>
                        <div
                            className="logoMainContainerInner"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (isPublic) {
                                    window.open('https://beta.symphonyos.co/', '_blank')
                                } else {
                                    history.push('/')
                                }
                            }}
                        >
                            <img
                                className="symphonyLogo"
                                style={sty.symphonyLogo}
                                src={symphonyLogo}
                                alt="symphonyLogo"
                            />
                            <div
                                className="betaText"
                                style={{
                                    marginTop: 4,
                                    color: Colors.greyNavbar,
                                    fontSize: 12,
                                    fontWeight: 500,
                                }}
                            >
                                {/* v{VERSION}*/}
                            </div>
                        </div>
                        {!isPublic &&
                            <SymphonyTooltip
                                title={isCollapsed ? "Always Show" : "Collapse"}
                                placement="right"
                            >
                                <div className="sidebarToggleIconContainer">
                                    <Icon
                                        className="sidebarToggleIcon"
                                        icon="expandDrawer"
                                        onClick={() => setIsCollapsed(!isCollapsed)}
                                        size={20}
                                        color={isCollapsed ? Colors.greyNavbar : Colors.white}
                                    />
                                </div>
                            </SymphonyTooltip>
                        }
                    </div>
                }
            </div>
        )
    }

    /**
     * Renders the team onboarding button in the navigation based on user role and organization status
         * 
         * Logic flow:
         * 1. If user is an admin:
         *    - Show "Admin: Team Onboarding" button that links to admin onboarding page
         *    - Button text collapses to just "Team" when sidebar is small
         * 
         * 2. If user is not an admin:
         *    - Only show button if user doesn't belong to an organization yet
         *    - Shows "Start a Team" button that opens upgrade modal
         *    - Button text collapses to just "Team" when sidebar is small
         *    - If user already has an organization, returns null (no button shown)
         * 
         * @returns JSX.Element | null - The team onboarding button UI or null if not applicable
         */
    const renderTeamOnboardingButton = () => {
        return null
        if (isSymphonyAdmin) {
            return null
        }

        if (!organization) {
            return (
                <div
                    className="nonAdminTeamPlanContainer"
                    style={styx(
                        sty.desktopNavigationLink,
                        { color: Colors.greyNavbar }
                    )}
                    onClick={() => handleOpenModal({
                        source: "Desktop Navigation - Upgrade to Pro Button",
                        tab: "team",
                    })}
                >
                    <Icon
                        className="icon"
                        icon="audiences"
                        color={Colors.greyNavbar}
                    />
                    {!isSmallSidebar &&
                        <div style={sty.desktopNavigationText}>
                            Start a Team
                        </div>
                    }
                </div>
            )
        }

        return null
    }

    const renderNavigationItems = () => {
        return (
            <ul
                className="mainNavigationContainer"
                style={sty.mainNavigationContainer}
            >
                {navigationItems
                    .filter((navItem: NavigationItem) => navItem.showOnDesktopMainMenu)
                    .filter((navItem: NavigationItem) => {
                        if (isPublic) {
                            // On public routes, only show items marked as public or always available
                            return navItem.isPublic || navItem.isAvailableAlways;
                        } else {
                            // On authenticated routes, exclude items that are public-only, unless they're always available
                            return !navItem.isPublic || navItem.isAvailableAlways;
                        }
                    })
                    .map((navItem: NavigationItem, index: number) => {
                        const {
                            name,
                            href,
                            alternateHrefs,
                            dropdownItems,
                            icon,
                            smallName,
                            onClick,
                        } = navItem || {};

                        let showDropdown = dropdownOpenId === name

                        let isSelected = Boolean((selectedRoute === href) ||
                            (alternateHrefs && alternateHrefs.indexOf(selectedRoute) > -1) ||
                            (href && href !== '/' && selectedRoute.includes(href)))

                        const mainNavItem = (
                            <div
                                className="liNavItemContainer"
                                style={sty.liNavItemContainer}
                                onClick={(e) => {
                                    if (onClick) {
                                        onClick(e)
                                    }

                                    if (href) {
                                        history.push(href)
                                    }

                                    if (dropdownItems && dropdownItems.length > 0) {
                                        if (dropdownOpenId === navItem.name) {
                                            setDropdownOpenId(null)
                                        } else {
                                            setDropdownOpenId(name)
                                        }
                                    } else {
                                        setDropdownOpenId(null)
                                    }
                                }}
                            >
                                <Indicator selected={isSelected} />

                                {navItem.icon &&
                                    <Icon
                                        className="navItemIcon"
                                        icon={icon || ''}
                                        color={isSelected ? 'white' : Colors.greyNavbar}
                                    />
                                }
                                {!isSmallSidebar &&
                                    <div
                                        className="desktopNavigationTextLarge"
                                        style={styx(
                                            sty.desktopNavigationTextLarge,
                                            { color: isSelected ? 'white' : Colors.greyNavbar }
                                        )}
                                    >
                                        {isSmallSidebar ? smallName ? smallName : name : name}
                                    </div>
                                }

                                {/* NOTE: dropdown arrow */}
                                {!isSmallSidebar && dropdownItems && dropdownItems.length > 0 &&
                                    <Icon
                                        className="dropdownArrow"
                                        style={{ marginLeft: 'auto', marginRight: 16 }}
                                        icon={dropdownOpenId === name ? 'chevronUp' : 'chevronDown'}
                                        color={Colors.greyNavbar}
                                        onClick={() => {
                                            // if this is a dropdown, open it
                                            // otherwise handle onclick normally
                                            if (dropdownItems && dropdownItems.length > 0) {
                                                if (dropdownOpenId === name) {
                                                    setDropdownOpenId(null)
                                                } else {
                                                    setDropdownOpenId(name)
                                                }
                                            }
                                        }}
                                    />
                                }
                            </div>
                        )

                        let mainNavChildrenItems = null
                        if (!isSmallSidebar && dropdownItems) {
                            const filteredDropdownItems = dropdownItems.filter(item => item.showOnDesktopSubMenu);
                            mainNavChildrenItems = (
                                <div
                                    className="additionalItemsContainer"
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        paddingLeft: 55,
                                        paddingTop: 8,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {filteredDropdownItems.map((dropdownItem: NavigationItem, index: number) => {
                                        let isDropdownItemSelected = Boolean((selectedRoute === dropdownItem.href) || (dropdownItem.alternateHrefs && dropdownItem.alternateHrefs.indexOf(selectedRoute) > -1))

                                        return (
                                            <div
                                                key={makeKey(dropdownItem.name, name, index)}
                                                className="desktopNavigationNavItem"
                                                style={{
                                                    color: isDropdownItemSelected ? Colors.white : Colors.greyNavbar,
                                                }}
                                                onClick={(e) => {
                                                    if (dropdownItem.href) {
                                                        history.push(dropdownItem.href)
                                                    }
                                                    if (dropdownItem.onClick) {
                                                        dropdownItem.onClick(e)
                                                    }
                                                }}
                                            >
                                                {dropdownItem.name}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        }

                        return (
                            <Fragment key={makeKey(name, index)}>
                                {mainNavItem}
                                {mainNavChildrenItems && dropdownItems && showDropdown &&
                                    mainNavChildrenItems
                                }
                            </Fragment>
                        )
                    })
                }
            </ul>
        )
    }

    const renderDesktopNavigationInnerAbove = () => {
        return (
            <div className="desktopNavigationInnerAbove">
                {renderLogoContainer()}
                {renderNavigationItems()}
            </div>
        )
    }

    const renderActionButtons = () => {
        if (isSmallSidebar) {
            return null
        }

        return (
            <div className="actionButtonsContainer" style={{ paddingLeft: 16, paddingRight: 16 }}>
                {!isSmallSidebar && hasViewOnlyAccess ? (
                    <SubscriptionStatusIndicator
                        text="View Only Access"
                        showStars={false}
                        backgroundColor={Colors.VIEW_ONLY_BACKGROUND}
                        textColor={Colors.textColorWhite}
                    />
                ) : !isProBrand && !isTeamBrand && !isStarterBrand && !isGrowthBrand && !isTeamV2Brand && (
                    <SubscriptionStatusIndicator
                        text={renderUpgradePlanText()}
                        showStars
                        handleOpenModal={handleUpgradeClick}
                    />
                )}
            </div>
        );
    }

    const renderDevModeControls = () => {
        if (!isSymphonyAdmin) return null;

        return (
            <div className="devModeControlsContainer" style={sty.devModeControlsContainer}>
                {/* Dev Mode Toggle */}
                <div className="devModeToggle" style={sty.devModeToggle}>
                    <span style={sty.devModeLabel}>Dev</span>
                    <button
                        className="devModeToggleButton"
                        style={{
                            ...sty.devModeToggleButton,
                            ...(devMode ? sty.devModeToggleButton_active : sty.devModeToggleButton_inactive)
                        }}
                        onClick={() => setDevMode(!devMode)}
                        type="button"
                    >
                        <div
                            className="devModeToggleSlider"
                            style={{
                                ...sty.devModeToggleSlider,
                                ...(devMode ? sty.devModeToggleSlider_active : sty.devModeToggleSlider_inactive)
                            }}
                        />
                    </button>
                </div>

                {/* Demo Mode Toggle - Only show when dev mode is enabled */}
                {devMode && (
                    <div className="demoModeToggle" style={sty.demoModeToggle}>
                        <span style={sty.demoModeLabel}>Demo</span>
                        <button
                            className="demoModeToggleButton"
                            style={{
                                ...sty.demoModeToggleButton,
                                ...(demoMode ? sty.demoModeToggleButton_active : sty.demoModeToggleButton_inactive)
                            }}
                            onClick={() => setDemoMode(!demoMode)}
                            type="button"
                        >
                            <div
                                className="demoModeToggleSlider"
                                style={{
                                    ...sty.demoModeToggleSlider,
                                    ...(demoMode ? sty.demoModeToggleSlider_active : sty.demoModeToggleSlider_inactive)
                                }}
                            />
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderBottomNavigationButtons = () => {
        const filteredButtons = BOTTOM_NAVIGATION_BUTTONS.filter(button => {
            const { isAdminOnly = false, onlyForSymphonyBetaTestUsers = false } = button;

            if (isAdminOnly && !isSymphonyAdmin) {
                return false;
            }

            if (onlyForSymphonyBetaTestUsers && !isSymphonyBetaTestUser) {
                return false;
            }

            return true;
        });

        return (
            <>
                {/* Dev Mode Controls */}
                {renderDevModeControls()}
                
                {/* Bottom Navigation Buttons */}
                {filteredButtons.map((button, index) => {
                    const {
                        icon,
                        text,
                        href,
                    } = button;

                    return (
                        <div
                            key={makeKey(text, index)}
                            className="bottomNavigationButton"
                            onClick={() => history.push(href)}
                            style={sty.desktopNavigationLink}
                        >
                            <Icon
                                className="bottomNavigationButtonIcon"
                                icon={icon}
                                color={selectedRoute === href ? Colors.white : Colors.greyNavbar}
                            />
                            {!isSmallSidebar &&
                                <div
                                    className="bottomNavigationButtonText"
                                    style={styx(
                                        sty.desktopNavigationText,
                                        { color: selectedRoute === href ? Colors.white : Colors.greyNavbar }
                                    )}
                                >
                                    {text}
                                </div>
                            }
                        </div>
                    )
                })}
            </>
        )
    }

    const renderDesktopNavigationInnerBelow = () => {
        return (
            <div className="desktopNavigationInnerBelow">
                <div
                    className="actionButtonsContainer"
                    style={sty.actionButtonsContainer}
                >
                    {renderActionButtons()}
                    {/* // Notice: check if we should show the subscription status when is on active organization */}
                    {!isSmallSidebar && !isOrganizationReadyOrWaiting && (isProBrand && currentBrand?.subscription && !currentBrand?.subscription.details?.trialOver && currentBrand?.subscription.details?.trialDaysLeft) &&
                        <div
                            className="subscriptionStatusIndicatorContainerFromDesktopNavigation"
                            style={{
                                paddingLeft: 16,
                                paddingRight: 16,
                            }}
                        >
                            <SubscriptionStatusIndicator
                                className="subscriptionStatusIndicatorFromDesktopNavigation"
                                text={`${currentBrand?.subscription.details?.trialDaysLeft} Day${currentBrand?.subscription.details?.trialDaysLeft === 1 ? '' : 's'} Left on Trial`}
                            />
                        </div>
                    }
                    <div
                        className="brandDropdownContainer"
                        style={isSmallSidebar ?
                            { marginLeft: 6 }
                            :
                            { paddingLeft: 16, paddingRight: 16 }
                        }
                    >
                        <AdminId
                            className="adminIdDesktopNavigation"
                            id={currentUser?.id}
                            label="userId"
                        />
                        <div
                            className="brandDropdownContainerInner"
                            style={{
                                marginTop: 'auto',
                                borderRadius: 16,
                                backgroundColor: isSmallSidebar ? 'rgba(75, 85, 99, 0)' : 'rgba(75, 85, 99, 0.25)',
                            }}
                        >
                            <BrandDropdown
                                className="brandDropdownDesktopNavigation"
                                smallSidebar={isSmallSidebar}
                            />
                        </div>
                    </div>
                </div>

                {renderTeamOnboardingButton()}

                {renderBottomNavigationButtons()}

                {!isSmallSidebar &&
                    <AdminOptions />
                }
            </div>
        )
    }

    let collapsedSidebarStyles = null;

    if (isCollapsed) {
        collapsedSidebarStyles = {
            position: 'fixed',
            maxWidth: isHoverSmallSidebar ? NAV_BAR_WIDTH_EXPANDED : NAV_BAR_WIDTH_COLLAPSED,
            zIndex: 50,
        } as CSSProperties
    }

    // NOTE: render()
    return (
        <div
            className={clsx('desktopNavigationComponent', className)}
            style={styx(
                sty.desktopNavigationComponent,
                { height: cssCalc() },
                collapsedSidebarStyles,
            )}
            onMouseEnter={() => {
                if (isCollapsed) {
                    setIsHoverSmallSidebar(true);
                }
            }}
            onMouseLeave={() => {
                if (isCollapsed) {
                    setIsHoverSmallSidebar(false);
                }
            }}
        >
            {renderDesktopNavigationInnerAbove()}
            {!isPublic && renderDesktopNavigationInnerBelow()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    desktopNavigationComponent: {
        position: 'sticky',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: NAV_BAR_WIDTH_EXPANDED,
        backgroundColor: Colors.purpleDark,
        paddingTop: 24,
        paddingBottom: 16,
        flexShrink: 0,
        zIndex: 1,
    },
    actionButtonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
    },
    symphonyLogo: {
        width: 139,
    },
    logoMainContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingLeft: 20,
        paddingRight: 20,
    },
    mainNavigationContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'around',
        color: 'white',
        marginBottom: 'auto',
    },
    navItemContainer: {
        width: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    miniSymphonyLogo: {
        marginLeft: 12,
        marginBottom: 1,
        height: 40,
        width: 40,
    },
    liNavItemContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingLeft: 16,
        paddingTop: 16,
        paddingBottom: 16,
    },

    desktopNavigationLink: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingRight: 16,
        paddingLeft: 16,
        paddingTop: 8,
        paddingBottom: 8,
        gap: 8,
        fontWeight: 500,
    },
    desktopNavigationText: {
        transform: 'translateY(2px)',
    },
    desktopNavigationTextLarge: {
        fontSize: 18,
        fontWeight: 500,
        transform: 'translateY(2px)',
        marginLeft: 12,
    },

    // Dev Mode Controls Styles
    devModeControlsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 0',
        borderTop: `1px solid ${Colors.greyNavbar}40`,
        marginTop: 16,
    },
    devModeToggle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
    },
    devModeLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: Colors.greyNavbar,
    },
    devModeToggleButton: {
        position: 'relative',
        width: 36,
        height: 20,
        borderRadius: 10,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        outline: 'none',
    },
    devModeToggleButton_active: {
        backgroundColor: Colors.purple,
    },
    devModeToggleButton_inactive: {
        backgroundColor: Colors.greyNavbar,
    },
    devModeToggleSlider: {
        position: 'absolute',
        top: 2,
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
    },
    devModeToggleSlider_active: {
        left: 18,
    },
    devModeToggleSlider_inactive: {
        left: 2,
    },
    demoModeToggle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
    },
    demoModeLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: Colors.greyNavbar,
    },
    demoModeToggleButton: {
        position: 'relative',
        width: 36,
        height: 20,
        borderRadius: 10,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        outline: 'none',
    },
    demoModeToggleButton_active: {
        backgroundColor: Colors.green,
    },
    demoModeToggleButton_inactive: {
        backgroundColor: Colors.greyNavbar,
    },
    demoModeToggleSlider: {
        position: 'absolute',
        top: 2,
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
    },
    demoModeToggleSlider_active: {
        left: 18,
    },
    demoModeToggleSlider_inactive: {
        left: 2,
    },
}

export default DesktopNavigation